import { Card } from '@app/app/_components/Card/Card';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { WizardStep } from '@app/app/_components/Wizard/Wizard';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import {
    PolicyholderFormFields,
    PolicyholderFormHandlers,
} from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/usePolicyholderFormFields';
import { InsuranceRisksStep } from '@app/app/esales/personal-accidents/_sections/steps/InsuranceRisksStep/InsuranceRisksStep';
import { InsuranceStartDateStep } from '@app/app/esales/personal-accidents/_sections/steps/InsuranceStartDateStep';
import { InsuredPeopleStep } from '@app/app/esales/personal-accidents/_sections/steps/InsuredPeopleStep/InsuredPeopleStep';
import { PA_INSURANCE_STEPS } from '@app/app/esales/personal-accidents/_sections/steps/PA_INSURANCE_STEPS';
import { PaymentMethodTypeStep } from '@app/app/esales/personal-accidents/_sections/steps/PaymentMethodTypeStep/PaymentMethodTypeStep';
import { PaymentOptionsStep } from '@app/app/esales/personal-accidents/_sections/steps/PaymentOptionsStep/PaymentOptionsStep';
import { PolicyholderStep } from '@app/app/esales/personal-accidents/_sections/steps/PolicyholderStep/PolicyholderStep';
import { SummaryStep } from '@app/app/esales/personal-accidents/_sections/steps/SummaryStep/SummaryStep';
import { PaymentMethodStep } from '@app/app/esales/_sections/steps/PaymentMethodStep';
import { CustomerLoginStep } from '@app/app/esales/_sections/steps/CustomerLoginStep';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Client } from '@app/domain/model/Client';
import { PlatformSettings } from '@app/domain/model/PlatformSettings';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { PersonalInsurancePremiumResponse } from '@app/domain/model/eshop/personal-accidents/PersonalInsurancePremiumResponse';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { PersonalAccidentsInsuranceRequestResult } from '@app/service/eshop/personal-accidents/actions/requestPersonalAccidentsInsurance';
import { ChangeEvent } from 'react';
import { InsuredPerson } from '@app/domain/model/eshop/personal-accidents/InsuredPerson';
import { PersonalInsuranceRiskPackage } from '@app/domain/model/eshop/personal-accidents/PersonalInsuranceRiskPackage';

interface Props {
    t: (key: string) => string;
    currentPremiums: Nullable<PersonalInsurancePremiumResponse>;
    selectedRiskCode: string;
    selectedRisk: Nullable<PersonalInsuranceRiskPackage>;
    totalPrice: number;
    priceToPayNow: number;
    handleRisksChange: (e: ChangeEvent<HTMLInputElement>) => void;
    policyholderFields: PolicyholderFormFields;
    policyholderHandlers: PolicyholderFormHandlers;
    formState: FormState<PurchasePersonalInsuranceResult>;
    requestResult: Nullable<PersonalAccidentsInsuranceRequestResult>;
    insuredPersons: InsuredPerson[];
    handleNameChange: (id: string) => (e: ChangeEvent<HTMLInputElement>) => void;
    handleSurnameChange: (id: string) => (e: ChangeEvent<HTMLInputElement>) => void;
    handlePersonalCodeChange: (id: string) => (e: ChangeEvent<HTMLInputElement>) => void;
    onChangeNumberOfPersons: () => void;
    editPolicyholderInfo: () => void;
    changeAgeOfPerson: () => void;
    onSavePolicyholder: (data: PolicyholderFormFields) => void;
    editInsuredPeople: () => void;
    editPolicy: () => void;
    editBeginDate: () => void;
    paymentFrequency: number;
    onPaymentFrequencyChange: (paymentFrequency: number) => void;
    handlePaymentMethodTypeChange: (paymentMethodType: Nullable<string>) => void;
    selectedPaymentMethodType: Nullable<string>;
    shouldSkipInsuredPeopleStep: boolean;
    authState: AuthState;
    clients: Client[];
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    onSuccessfulLogin: () => void;
    settings: PlatformSettings;
    initialBeginDate?: string;
}

export function buildSteps({
    t,
    currentPremiums,
    selectedRiskCode,
    selectedRisk,
    totalPrice,
    priceToPayNow,
    handleRisksChange,
    policyholderFields,
    policyholderHandlers,
    formState,
    requestResult,
    insuredPersons,
    handleNameChange,
    handleSurnameChange,
    handlePersonalCodeChange,
    onChangeNumberOfPersons,
    editPolicyholderInfo,
    changeAgeOfPerson,
    onSavePolicyholder,
    editInsuredPeople,
    editPolicy,
    editBeginDate,
    paymentFrequency,
    onPaymentFrequencyChange,
    handlePaymentMethodTypeChange,
    selectedPaymentMethodType,
    shouldSkipInsuredPeopleStep,
    authState,
    clients,
    swedbankLogoSrc,
    sebLogoSrc,
    onSuccessfulLogin,
    settings,
    initialBeginDate,
}: Props): WizardStep[] {
    return [
        {
            label: '',
            subSteps: [
                {
                    key: PA_INSURANCE_STEPS.CHOOSE_INSURANCE,
                    title: (
                        <div className="flex w-full flex-row items-center justify-between gap-4">
                            {t('eshop.personalAccidents.chooseInsurance')}
                            <InformationPopover
                                className="absolute right-0"
                                content={<Paragraph>{t('eshop.personalAccidents.chooseInsuranceInfo')}</Paragraph>}>
                                <QuestionCircleIcon className="size-6 text-neutral-500" />
                            </InformationPopover>
                        </div>
                    ),
                    body: (
                        <InsuranceRisksStep
                            onChange={handleRisksChange}
                            risks={currentPremiums?.riskPackages ?? []}
                            value={selectedRiskCode ? selectedRiskCode : undefined}
                        />
                    ),
                },
                {
                    key: PA_INSURANCE_STEPS.BEGIN_DATE,
                    title: t('eshop.personalAccidents.insuranceStartDate'),
                    body: <InsuranceStartDateStep initialBeginDate={initialBeginDate} />,
                },
                {
                    key: PA_INSURANCE_STEPS.CUSTOMER_LOGIN,
                    when: () =>
                        settings.general.esalesPersonalAccidentsAuthEnabled &&
                        (!authState.authenticated || !authState.clientSelected),
                    title: t('eshop.general.ifYouAlreadyClient'),
                    body: (
                        <CustomerLoginStep
                            swedbankLogoSrc={swedbankLogoSrc}
                            sebLogoSrc={sebLogoSrc}
                            clients={clients}
                            authState={authState}
                            onContinue={onSuccessfulLogin}
                            onSuccess={onSuccessfulLogin}
                            bankAuthEnabled={settings.general.esalesPersonalAccidentsBankAuthEnabled}
                        />
                    ),
                },
                {
                    key: PA_INSURANCE_STEPS.POLICYHOLDER,
                    title: t('eshop.personalAccidents.policyholder'),
                    body: (
                        <Card className="flex flex-col gap-8 p-6">
                            <PolicyholderStep
                                formState={formState}
                                personalCode={requestResult?.policyHolderCode ?? ''}
                                policyholderFields={policyholderFields}
                                policyholderHandlers={policyholderHandlers}
                            />
                        </Card>
                    ),
                },
                {
                    key: PA_INSURANCE_STEPS.INSURED_PERSONS,
                    when: () => !shouldSkipInsuredPeopleStep,
                    title: t('eshop.personalAccidents.insuredPersons'),
                    body: (
                        <InsuredPeopleStep
                            insuredPersons={insuredPersons}
                            handleNameChange={handleNameChange}
                            handleSurnameChange={handleSurnameChange}
                            handlePersonalCodeChange={handlePersonalCodeChange}
                            violations={formState.violations}
                            policyholderIsInsuredPerson={policyholderFields.isInsuredPerson}
                            onChangeNumberOfPersons={onChangeNumberOfPersons}
                            formState={formState}
                            editPolicyholderInfo={editPolicyholderInfo}
                            changeAgeOfPerson={changeAgeOfPerson}
                        />
                    ),
                },
                {
                    key: PA_INSURANCE_STEPS.SUMMARY,
                    title: t('eshop.personalAccidents.summary'),
                    body: (
                        <SummaryStep
                            formState={formState}
                            requestResult={requestResult}
                            policyholderFields={policyholderFields}
                            personalCode={requestResult?.policyHolderCode ?? ''}
                            onSavePolicyholder={onSavePolicyholder}
                            insuredPersons={insuredPersons}
                            onChangeNumberOfPersons={onChangeNumberOfPersons}
                            editPolicyholderInfo={editPolicyholderInfo}
                            editInsuredPeople={editInsuredPeople}
                            editPolicy={editPolicy}
                            editBeginDate={editBeginDate}
                            selectedRisk={selectedRisk ?? null}
                            totalPrice={totalPrice}
                            priceToPayNow={priceToPayNow}
                            paymentFrequency={paymentFrequency}
                            onPaymentFrequencyChange={onPaymentFrequencyChange}
                        />
                    ),
                },
            ],
        },
        {
            label: t('eshop.personalAccidents.paymentStepTitle'),
            subSteps: [
                {
                    key: PA_INSURANCE_STEPS.PAYMENT.OPTIONS,
                    title: (
                        <div className="flex w-full flex-row items-center justify-between gap-4">
                            {t('eshop.personalAccidents.choosePaymentOptions')}
                            <InformationPopover content={t('eshop.personalAccidents.paymentOptionsTooltip')}>
                                <QuestionCircleIcon className="size-5 text-neutral-500" />
                            </InformationPopover>
                        </div>
                    ),
                    body: <PaymentOptionsStep formState={formState} paymentFrequency={paymentFrequency} />,
                },
                {
                    key: PA_INSURANCE_STEPS.PAYMENT.METHOD_TYPE,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodTypeStep formState={formState} onChange={handlePaymentMethodTypeChange} />,
                },
                {
                    key: PA_INSURANCE_STEPS.PAYMENT.METHOD,
                    when: () => selectedPaymentMethodType === PaymentMethodType.Banks,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodStep paymentMethods={formState.data?.paymentMethods ?? []} />,
                },
            ],
        },
    ];
}
